import * as THREE from 'three';

const MAX_DAYS = 4; // Legend & lines: last N days

export class PanelInfo {
  canvas: HTMLCanvasElement;
  texture: THREE.Texture;
  material: THREE.SpriteMaterial;
  sprite: THREE.Sprite;
  private lastPanel?: THREE.Mesh;
  private lastDrawSignature = '';

  // Neon-Palette (zyan → violet → amber → grün → rose → gelb → indigo)
  readonly dayColors = ['#38bdf8', '#a855f7', '#f59e0b', '#22c55e', '#f43f5e', '#eab308', '#6366f1'];

  private infoOffsetY = 20;

  setVisible(v: boolean) {
    const wasVisible = this.sprite.visible;
    this.sprite.visible = v;
    if (v && !wasVisible && this.lastPanel) this.update(this.lastPanel, true);
  }
  isVisible() { return this.sprite.visible; }

  constructor(scene: THREE.Scene, panel: THREE.Mesh, uiScale = 1.45, infoOffsetY = 20) {
    this.infoOffsetY = infoOffsetY;

    const BASE_W = 360;
    const BASE_H = 520;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) ? window.devicePixelRatio : 1;

    this.canvas = document.createElement('canvas');
    this.canvas.width  = Math.round(BASE_W * dpr);
    this.canvas.height = Math.round(BASE_H * dpr);

    const ctx = this.canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    this.material = new THREE.SpriteMaterial({
      map: this.texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.sprite = new THREE.Sprite(this.material);

    const worldScale = 0.062 * uiScale;
    this.sprite.scale.set(BASE_W * worldScale, BASE_H * worldScale, 1);

    // Startposition (leicht über dem Panel)
    const baseOffsetY = (BASE_H * worldScale) / 2 + 0.5;
    this.sprite.position.copy(panel.position.clone().add(new THREE.Vector3(0, baseOffsetY + this.infoOffsetY, 0)));
    this.sprite.renderOrder = 999;
    this.sprite.visible = false;

    scene.add(this.sprite);
    this.update(panel);
  }

  // ---------- helpers ----------
  private roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 14) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
  private niceNum(range: number, round: boolean) {
    const exp = Math.floor(Math.log10(Math.max(range, 1e-9)));
    const f = range / Math.pow(10, exp);
    let nf: number;
    if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    else       nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * Math.pow(10, exp);
  }
  private computeNiceScale(min: number, max: number, tickCount = 6) {
    const range = this.niceNum(max - min || 1, false);
    const step  = this.niceNum(range / (tickCount - 1), true);
    const niceMin = Math.floor(min / step) * step;
    const niceMax = Math.ceil(max / step) * step;
    return { niceMin, niceMax, step };
  }
  private drawGlowLine(ctx: CanvasRenderingContext2D, pts: Array<{x:number,y:number}>, color: string) {
    if (pts.length < 2) return;
    // Glow
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = 10;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i=1;i<pts.length;i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.stroke();
    ctx.restore();
    // Crisp line
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i=1;i<pts.length;i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.stroke();
    ctx.restore();
  }
  private chip(ctx: CanvasRenderingContext2D, x:number, y:number, text:string, color='rgba(56,189,248,.35)') {
    ctx.save();
    const padX=8, r=10;
    ctx.font = '600 14px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
    const w = ctx.measureText(text).width + padX*2;
    const h = 22;
    this.roundedRect(ctx, x, y, w, h, r);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.18)';
    ctx.stroke();
    ctx.fillStyle = '#EAF2FF';
    ctx.fillText(text, x+padX, y+15);
    ctx.restore();
    return {w, h};
  }

  private readPowerKW(entry: { energy?: number; deltaHours?: number; powerKW?: number }) {
    if (Number.isFinite(entry.powerKW)) return Math.max(0, entry.powerKW as number);
    if (Number.isFinite(entry.energy) && Number.isFinite(entry.deltaHours) && (entry.deltaHours as number) > 0) {
      return Math.max(0, (entry.energy as number) / (entry.deltaHours as number));
    }
    // Legacy history entries did not store deltaHours/powerKW. Keep them drawable as a best-effort fallback.
    if (Number.isFinite(entry.energy)) return Math.max(0, entry.energy as number);
    return null;
  }

  // ---------- main draw ----------
  private updatePosition(panel: THREE.Mesh) {
    const BASE_H = 520;
    const currentWorldScale = this.sprite.scale.y / BASE_H;
    const baseOffsetY = (BASE_H * currentWorldScale) / 2 + 0.5;
    const extraY = (panel as any).userData?.labelOffsetY ?? this.infoOffsetY;
    this.sprite.position.set(panel.position.x, panel.position.y + baseOffsetY + extraY, panel.position.z);
  }

  private drawSignature(panel: THREE.Mesh) {
    const history = ((panel as any).energyHistory as Array<any> | undefined) ?? [];
    const last = history[history.length - 1];
    return JSON.stringify({
      name: (panel as any).name ?? panel.name,
      type: (panel as any).type,
      day: (panel as any).energyDay ?? 0,
      total: (panel as any).energyTotal ?? 0,
      historyLength: history.length,
      lastTime: last?.time,
      lastEnergy: last?.energy,
      lastPowerKW: last?.powerKW,
      lastDay: last?.day,
    });
  }

  update(panel: THREE.Mesh, force = false) {
    this.lastPanel = panel;
    this.updatePosition(panel);
    if (!force && !this.sprite.visible) return;

    const signature = this.drawSignature(panel);
    if (!force && signature === this.lastDrawSignature) return;
    this.lastDrawSignature = signature;

    const BASE_W = 360;
    const BASE_H = 520;

    const ctx = this.canvas.getContext('2d')!;
    const W = BASE_W, H = BASE_H;

    ctx.clearRect(0, 0, W, H);

    // Outer glow border
    const borderGrad = ctx.createLinearGradient(0, 0, W, H);
    borderGrad.addColorStop(0,  'rgba(94,234,212,.85)');
    borderGrad.addColorStop(1,  'rgba(56,189,248,.85)');

    // Background (terminal glass)
    this.roundedRect(ctx, 4, 4, W-8, H-8, 14);
    ctx.fillStyle = 'rgba(6,14,20,0.94)';
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,.06)';
    ctx.stroke();

    // Gradient stroke ring
    this.roundedRect(ctx, 3.5, 3.5, W-7, H-7, 15);
    ctx.lineWidth = 2;
    ctx.strokeStyle = borderGrad;
    ctx.globalAlpha = 0.9;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Terminal-Titelleiste (Ampel-Punkte, wie Splash/Login) statt Glow-Dot
    ctx.save();
    ctx.strokeStyle = 'rgba(94,234,212,.2)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(4, 38); ctx.lineTo(W-4, 38); ctx.stroke();
    const dotY = 21;
    ['#ff5f56', '#ffbd2e', '#27c93f'].forEach((c, i) => {
      ctx.beginPath();
      ctx.arc(20 + i * 15, dotY, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = c;
      ctx.fill();
    });
    ctx.fillStyle = 'rgba(148,197,255,.55)';
    ctx.font = '600 11px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
    ctx.fillText('tracker@core — info', 74, dotY + 4);
    ctx.restore();

    // Header
    ctx.save();
    ctx.translate(16, 54);
    ctx.fillStyle = '#5eead4';
    ctx.shadowColor = 'rgba(94,234,212,.6)';
    ctx.shadowBlur = 8;
    ctx.font = '800 17px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
    ctx.fillText('PANEL_INFO', 0, 0);
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(148,197,255,.7)';
    ctx.font = '600 13px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
    ctx.fillText('digital twin — solar tracker', 0, 20);
    ctx.restore();

    // Chart layout
    const padL = 74, padR = 18, padT = 90; // etwas mehr Platz für die neue zweizeilige Terminal-Kopfzeile
    const chartX = 12 + padL;
    const chartY = padT;
    const chartW = W - 24 - padL - padR;
    const chartH = 230;

    // Chart background
    this.roundedRect(ctx, chartX-8, chartY-8, chartW+16, chartH+16, 12);
    const cg = ctx.createLinearGradient(0, chartY-8, 0, chartY+chartH+8);
    cg.addColorStop(0, 'rgba(255,255,255,0.04)');
    cg.addColorStop(1, 'rgba(255,255,255,0.02)');
    ctx.fillStyle = cg; ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.stroke();

    // Data
    const history = (panel as any).energyHistory as {
      time: string; minutesOfDay?: number; energy?: number; deltaHours?: number; powerKW?: number; day?: number;
    }[] | undefined;

    if (history && history.length) {
      const normalized = history
        .map(e => ({ ...e, minutesOfDay: e.minutesOfDay ?? 0, powerKW: this.readPowerKW(e) }))
        .filter(e => e.powerKW != null);

      const allDays = [...new Set(normalized.map(e => e.day ?? 0))].sort((a,b)=>a-b);
      const days = allDays.slice(-MAX_DAYS);

      const vals = normalized.filter(e => days.includes(e.day ?? 0)).map(e => e.powerKW ?? 0);
      const dataMin = 0;
      const dataMax = Math.max(...vals, 0.0001);
      const { niceMin, niceMax, step } = this.computeNiceScale(dataMin, dataMax, 6);

      const xOf = (m: number) => chartX + (m / 1440) * chartW;
      const yOf = (v: number) => chartY + chartH - ((v - niceMin) / (niceMax - niceMin)) * chartH;

      // Grid
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      for (let h = 0; h <= 24; h += 4) {
        const x = xOf(h * 60);
        ctx.beginPath(); ctx.moveTo(x, chartY); ctx.lineTo(x, chartY + chartH); ctx.stroke();
      }
      for (let yVal = niceMin; yVal <= niceMax + 1e-9; yVal += step) {
        const y = yOf(yVal);
        ctx.beginPath(); ctx.moveTo(chartX, y); ctx.lineTo(chartX + chartW, y); ctx.stroke();
      }
      ctx.restore();

      // Axis frame
      ctx.strokeStyle = 'rgba(255,255,255,.85)';
      ctx.lineWidth = 1.25;
      ctx.strokeRect(chartX, chartY, chartW, chartH);

      // Lines per day (with glow)
      days.forEach(day => {
        const color = this.dayColors[day % this.dayColors.length];
        const pts = normalized
          .filter(e => (e.day ?? 0) === day)
          .sort((a,b)=> (a.minutesOfDay! - b.minutesOfDay!))
          .map(p => ({ x: xOf(p.minutesOfDay!), y: yOf(p.powerKW ?? 0) }));
        this.drawGlowLine(ctx, pts, color);
      });

      // X ticks & title
      ctx.fillStyle = '#E8EEF8';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.font = '600 13px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
      for (let h = 0; h <= 24; h += 4) ctx.fillText(`${h}h`, xOf(h * 60), chartY + chartH + 18);
      ctx.font = '700 14px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
      ctx.fillText('Zeit', chartX + chartW / 2, chartY + chartH + 36);

      // Y ticks & title
      ctx.textAlign = 'right';
      ctx.font = '600 13px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
      for (let yVal = niceMin; yVal <= niceMax + 1e-9; yVal += step) {
        const y = yOf(yVal);
        const label = yVal.toFixed(step >= 1 ? 0 : (step >= 0.1 ? 1 : 2));
        const tx = chartX - 10;
        // small backplate
        const w = ctx.measureText(label).width + 10;
        const h = 18;
        this.roundedRect(ctx, tx - w, y - 12, w, h, 6);
        ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.fill();
        ctx.fillStyle = '#E8EEF8'; ctx.fillText(label, tx, y+2);
      }
      ctx.save();
      ctx.translate(chartX - 52, chartY + chartH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.font = '700 14px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
      ctx.fillStyle = '#E8EEF8';
      ctx.fillText('Leistung [kW]', 0, 0);
      ctx.restore();
    }

    // Info section
    const infoY = chartY + chartH + 56;
    ctx.fillStyle = '#E8EEF8';
    ctx.textAlign = 'left';

    // Chips: Name & Type — untereinander statt nebeneinander, sonst läuft der Type-Chip bei
    // langen Tracker-Namen (z.B. "dual_west-unlimited_35") über den Kartenrand hinaus.
    const name = (panel as any).name ?? panel.name ?? 'panel';
    const type = (panel as any).type ?? '—';
    const chip1 = this.chip(ctx, 16, infoY - 6, `Name: ${name}`, 'rgba(56,189,248,.22)');
    this.chip(ctx, 16, infoY - 6 + chip1.h + 6, `Type: ${type}`, 'rgba(168,85,247,.22)');
    const chipsExtraH = chip1.h + 6;

    // KPIs
    ctx.font = '600 16px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
    ctx.fillText(`🔋 Today: ${( (panel as any).energyDay ?? 0 ).toFixed(2)} kWh`, 16, infoY + 36 + chipsExtraH);
    ctx.fillText(`📊 Total: ${( (panel as any).energyTotal ?? 0 ).toFixed(2)} kWh`, 16, infoY + 60 + chipsExtraH);

    // Legend (last N days)
    const hist = (panel as any).energyHistory as { day?: number }[] | undefined;
    if (hist && hist.length) {
      const legendDays = [...new Set(hist.map(e => e.day ?? 0))].sort((a,b)=>a-b).slice(-MAX_DAYS);
      ctx.font = '700 14px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
      ctx.fillStyle = '#E8EEF8';
      ctx.fillText('Days:', 16, infoY + 88 + chipsExtraH);
      let x = 60;
      legendDays.forEach((d) => {
        const c = this.dayColors[d % this.dayColors.length];
        ctx.save();
        this.roundedRect(ctx, x, infoY + 72 + chipsExtraH, 40, 22, 8);
        ctx.fillStyle = c + '33'; // translucent fill
        ctx.fill(); ctx.strokeStyle = c; ctx.stroke();
        ctx.fillStyle = '#E8EEF8';
        ctx.textAlign = 'center';
        ctx.font = '700 13px ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
        ctx.fillText(String(d), x + 20, infoY + 88 + chipsExtraH);
        ctx.restore();
        x += 48;
      });
    }

    // push texture
    this.texture.needsUpdate = true;

  }

  dispose() {
    this.sprite.parent?.remove(this.sprite);
    this.texture.dispose();
    this.material.dispose();
  }
}
